import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

import type { MarketingDecision, MarketingPost } from "@/lib/api";
import { cn } from "@/lib/formatters";

interface PostRunsProps {
  posts: MarketingPost[];
  picked: { slug: string; platform: string } | null;
  onPick: (slug: string, platform: string) => void;
}

// Cuatro desenlaces, cuatro lecturas distintas. `saltado` se apaga a propósito:
// es el caso que no costó nada y no requiere atención.
const HUE: Record<MarketingDecision, string> = {
  publicar: "border-success/50 bg-success/10 text-success",
  suplente: "border-warn/50 bg-warn/10 text-warn",
  rechazar: "border-danger/40 bg-danger/5 text-danger",
  saltado:  "border-border bg-bg-elev/40 text-muted/60",
};

// El mismo código de color reducido a un punto, para la fila plegada.
const PUNTO: Record<MarketingDecision, string> = {
  publicar: "bg-success",
  suplente: "bg-warn",
  rechazar: "bg-danger/60",
  saltado:  "bg-border",
};

const LABEL: Record<MarketingDecision, string> = {
  publicar: "publica",
  suplente: "suplente",
  rechazar: "rechazado",
  saltado:  "saltado",
};

const ICONO: Record<string, string> = {
  linkedin: "💼", instagram: "📸", x: "𝕏", threads: "🧵",
  facebook: "📘", reddit: "🟠", tiktok: "🎵",
};

/**
 * Un post por fila, plegado.
 *
 * Antes cada post ocupaba una tarjeta con siete fichas desplegadas, así que
 * treinta posts eran un scroll interminable donde todo pesaba igual. Ahora la
 * fila cerrada dice lo único que se mira de corrido —qué publica y qué falta
 * producir— y el detalle aparece al abrir.
 */
export function PostRuns({ posts, picked, onPick }: PostRunsProps) {
  // El más reciente abierto: es el que casi siempre se viene a mirar.
  const [abierto, setAbierto] = useState<string | null>(posts[0]?.slug ?? null);

  return (
    <div className="space-y-2">
      {posts.map((post) => {
        const expandido = abierto === post.slug;
        const publican = post.platforms.filter((p) => p.decision === "publicar");
        const producir = publican.filter((p) => p.needs_production);

        return (
          <div key={post.slug} className="surface overflow-hidden">
            <button
              type="button"
              onClick={() => setAbierto(expandido ? null : post.slug)}
              aria-expanded={expandido}
              className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-bg-elev/40 transition-colors"
            >
              <span
                aria-hidden
                className={cn(
                  "font-mono text-xs text-muted transition-transform shrink-0",
                  expandido && "rotate-90",
                )}
              >
                ▸
              </span>

              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-medium leading-snug truncate">
                  {post.title || post.slug}
                </h3>
                <p className="font-mono text-[10px] text-muted mt-0.5 truncate">
                  {post.week && `${post.week} · `}
                  {post.category}
                </p>
              </div>

              {/* Un punto por red: el estado del post entero en un vistazo. */}
              <div className="flex gap-1 shrink-0" aria-hidden>
                {post.platforms.map((p) => (
                  <span
                    key={p.platform}
                    title={`${p.platform}: ${LABEL[p.decision]}`}
                    className={cn("w-1.5 h-4 rounded-full", PUNTO[p.decision])}
                  />
                ))}
              </div>

              <div className="font-mono text-[10px] text-muted tabular-nums shrink-0 w-24 text-right">
                {publican.length > 0 ? `${publican.length} publica` : "nada publica"}
                {producir.length > 0 && (
                  <span className="block text-warn">🎬 {producir.length} a producir</span>
                )}
              </div>
            </button>

            <AnimatePresence initial={false}>
              {expandido && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.18 }}
                >
                  <div className="px-4 pb-4 space-y-3 border-t border-border/60 pt-3">
                    <div className="flex items-center justify-between font-mono text-[10px] text-muted">
                      <span className="truncate">{post.slug}</span>
                      <span className="tabular-nums shrink-0 ml-3">
                        {post.floor !== null && `piso ${post.floor} · `}
                        {post.usage.output_tokens.toLocaleString()} out
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                      {post.platforms.map((p) => {
                        const active =
                          picked?.slug === post.slug && picked?.platform === p.platform;
                        const clickable = p.has_content;
                        return (
                          <button
                            key={p.platform}
                            type="button"
                            disabled={!clickable}
                            onClick={() => clickable && onPick(post.slug, p.platform)}
                            title={p.reason || p.fit_why}
                            className={cn(
                              "px-2.5 py-2 rounded border text-left transition-all",
                              HUE[p.decision],
                              clickable
                                ? "hover:brightness-125 cursor-pointer"
                                : "cursor-default",
                              active && "ring-1 ring-primary/70",
                            )}
                          >
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="font-mono text-[11px] truncate">
                                <span aria-hidden className="mr-1">
                                  {ICONO[p.platform] ?? "•"}
                                </span>
                                {p.platform}
                              </span>
                              <span className="font-mono text-sm tabular-nums">
                                {p.score ?? "—"}
                              </span>
                            </div>
                            <div className="flex items-baseline justify-between gap-2 mt-0.5">
                              <span className="font-mono text-[9px] uppercase tracking-wider opacity-80">
                                {/* El formato importa tanto como la red: un
                                    carrusel y un texto no son el mismo trabajo. */}
                                {p.formato ?? LABEL[p.decision]}
                              </span>
                              {p.fit !== null && (
                                <span className="font-mono text-[9px] opacity-60 tabular-nums">
                                  fit {p.fit}
                                </span>
                              )}
                            </div>
                            {p.needs_production && p.decision === "publicar" && (
                              <div className="font-mono text-[9px] mt-1 opacity-70">
                                🎬 producir
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {!post.platforms.some((p) => p.has_content) && (
                      <p className="text-xs text-muted">
                        Ninguna red de este post tiene contenido guardado.
                      </p>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}
